import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import th from '@/locales/th/common.json';
import en from '@/locales/en/common.json';
import zh from '@/locales/zh/common.json';
import lo from '@/locales/lo/common.json';
import my from '@/locales/my/common.json';
import vi from '@/locales/vi/common.json';
import km from '@/locales/km/common.json';
import { buildGuideResources } from './guide-resources.js';

type Bundles = Record<string, Record<string, Record<string, unknown>>>;

/**
 * Fold the per-domain help copy in as a second namespace beside `common`.
 *
 * Help copy is kept out of `common.json` both to stop that file doubling in
 * size and, more importantly, so each domain owns its own files — which is
 * what lets many domains be written at once without conflicting on one
 * catalog. A language a domain has not been translated into contributes
 * nothing and falls back per key down `FALLBACK_LANGUAGES`.
 */
function mergeGuideResources(base: Bundles): Bundles {
  const guide = buildGuideResources();
  const out: Bundles = {};
  for (const [lang, bundles] of Object.entries(base)) {
    out[lang] = { ...bundles, guide: guide[lang]?.guide ?? {} };
  }
  return out;
}

export const SUPPORTED_LANGUAGES = [
  { code: 'th', name: 'ไทย', nativeLabel: 'ไทย' },
  { code: 'en', name: 'English', nativeLabel: 'English' },
  { code: 'zh', name: '中文', nativeLabel: '中文' },
  { code: 'lo', name: 'ລາວ', nativeLabel: 'ລາວ' },
  { code: 'my', name: 'မြန်မာ', nativeLabel: 'မြန်မာ' },
  { code: 'vi', name: 'Tiếng Việt', nativeLabel: 'Tiếng Việt' },
  { code: 'km', name: 'ខ្មែរ', nativeLabel: 'ខ្មែរ' },
] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]['code'];

export const DEFAULT_LANGUAGE: SupportedLanguage = 'th';

/**
 * Where a key that the chosen language does not define is looked for next.
 *
 * English first, Thai last. km/lo/my/vi/zh are each hundreds of keys short of
 * `en`, and while this was the single string 'th' every one of those holes put
 * THAI on a Khmer, Lao, Burmese, Vietnamese or Chinese operator's screen — on
 * the promotion-config screens above all, where a misread field costs money.
 * English is the one language every operator here can fall back to.
 *
 * Thai stays LAST because it is the only complete catalog: a key `en` happens
 * not to carry still resolves instead of rendering as its raw dotted path,
 * which is the failure `i18n-parity.test.ts` exists to catch. Order is
 * load-bearing in both directions — the test derives its floor from the last
 * entry here.
 */
export const FALLBACK_LANGUAGES: readonly SupportedLanguage[] = ['en', 'th'];

export const LANGUAGE_STORAGE_KEY = 'b1fr0st.language';

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: mergeGuideResources({
      th: { common: th },
      en: { common: en },
      zh: { common: zh },
      lo: { common: lo },
      my: { common: my },
      vi: { common: vi },
      km: { common: km },
    }),
    fallbackLng: [...FALLBACK_LANGUAGES],
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
    ns: ['common', 'guide'],
    defaultNS: 'common',
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
    },
  });

// Keep <html lang="..."> in sync so our CSS font rules apply.
i18n.on('languageChanged', (lng) => {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lng;
  }
});

export default i18n;
